/**
 * Gobodo Flow Service – CRUD for Flow__c.
 * Full definition in DefinitionJson__c; scalars (Name, Type, Status, ApiName, Description) for list.
 */

import type { Flow, FlowVariable, FlowElement, FlowStats, CreateFlowPayload, UpdateFlowPayload, CloneFlowPayload } from '../../types/flowAutomation';
import {
  getGobodoRecords,
  getGobodoRecordById,
  createGobodoRecord,
  updateGobodoRecord,
  deleteGobodoRecord
} from './gobodoRecordService';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { getCustomSuffix } from '../../config/gobodoConfig';

const getObjectType = () => `Flow${getCustomSuffix()}`;
const FIELD = {
  NAME: 'Name__c',
  DESCRIPTION: 'Description__c',
  TYPE: 'Type__c',
  STATUS: 'Status__c',
  API_NAME: 'ApiName__c',
  DEFINITION_JSON: 'DefinitionJson__c',
  CREATED_DATE: 'CreatedDate__c',
  LAST_MODIFIED: 'LastModified__c'
} as const;

export async function ensureFlowObjectExists(): Promise<void> {
  await ensureObjectTypeExists(getObjectType(), {
    label: 'Flow',
    pluralLabel: 'Flows',
    description: 'Flow definition for Flow Automation.',
    icon: 'Workflow',
    category: 'custom',
    isCustom: true,
    isActive: true
  });
}

function flowToRecord(f: Partial<Flow>): Record<string, unknown> {
  const record: Record<string, unknown> = {
    [FIELD.NAME]: f.name ?? '',
    [FIELD.DESCRIPTION]: f.description ?? '',
    [FIELD.TYPE]: f.type ?? 'auto_launched',
    [FIELD.STATUS]: f.status ?? 'draft',
    [FIELD.API_NAME]: f.apiName ?? ''
  };
  if (f.createdDate) record[FIELD.CREATED_DATE] = f.createdDate;
  if (f.lastModifiedDate) record[FIELD.LAST_MODIFIED] = f.lastModifiedDate;
  record[FIELD.DEFINITION_JSON] = JSON.stringify({
    variables: f.variables,
    elements: f.elements,
    startElementId: f.startElementId,
    scheduleConfig: f.scheduleConfig,
    recordTriggerConfig: f.recordTriggerConfig,
    platformEventConfig: f.platformEventConfig,
    version: f.version,
    versions: f.versions,
    createdBy: f.createdBy,
    lastModifiedBy: f.lastModifiedBy,
    runCount: f.runCount,
    successCount: f.successCount,
    failureCount: f.failureCount,
    canvasSettings: f.canvasSettings
  });
  return record;
}

function recordToFlow(record: Record<string, any>): Flow {
  const id = record.id != null ? String(record.id) : '';
  let def: Record<string, any> = {};
  try {
    const raw = record[FIELD.DEFINITION_JSON];
    if (raw && typeof raw === 'string') def = JSON.parse(raw);
    else if (raw && typeof raw === 'object') def = raw;
  } catch {
    // no-op
  }
  return {
    id,
    name: record[FIELD.NAME] ?? '',
    apiName: record[FIELD.API_NAME] ?? '',
    description: record[FIELD.DESCRIPTION] ?? '',
    type: (record[FIELD.TYPE] as Flow['type']) ?? 'auto_launched',
    status: (record[FIELD.STATUS] as Flow['status']) ?? 'draft',
    variables: def.variables ?? [],
    elements: def.elements ?? [],
    startElementId: def.startElementId ?? '',
    scheduleConfig: def.scheduleConfig,
    recordTriggerConfig: def.recordTriggerConfig,
    platformEventConfig: def.platformEventConfig,
    version: def.version ?? 1,
    versions: def.versions ?? [],
    createdDate: record[FIELD.CREATED_DATE] ?? new Date().toISOString(),
    createdBy: def.createdBy ?? 'current_user',
    createdByName: def.createdByName ?? 'System Administrator',
    lastModifiedDate: record[FIELD.LAST_MODIFIED] ?? new Date().toISOString(),
    lastModifiedBy: def.lastModifiedBy ?? 'current_user',
    lastModifiedByName: def.lastModifiedByName ?? 'System Administrator',
    runCount: def.runCount ?? 0,
    successCount: def.successCount ?? 0,
    failureCount: def.failureCount ?? 0,
    canvasSettings: def.canvasSettings
  };
}

const generateId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export async function getFlows(): Promise<Flow[]> {
  await ensureFlowObjectExists();
  const { records } = await getGobodoRecords(getObjectType());
  return records.map((r: Record<string, any>) => recordToFlow(r));
}

export async function getFlowById(id: string): Promise<Flow | null> {
  const record = await getGobodoRecordById(id);
  if (!record) return null;
  return recordToFlow(record);
}

export async function getStats(): Promise<FlowStats> {
  const flows = await getFlows();
  const active = flows.filter(f => f.status === 'active').length;
  const draft = flows.filter(f => f.status === 'draft').length;
  const inactive = flows.filter(f => f.status === 'inactive').length;
  const totalRuns = flows.reduce((s, f) => s + (f.runCount || 0), 0);
  const successRuns = flows.reduce((s, f) => s + (f.successCount || 0), 0);
  const failedRuns = flows.reduce((s, f) => s + (f.failureCount || 0), 0);
  const byType: Record<string, number> = {};
  for (const f of flows) byType[f.type] = (byType[f.type] || 0) + 1;
  return {
    totalFlows: flows.length,
    activeFlows: active,
    draftFlows: draft,
    inactiveFlows: inactive,
    byType: byType as FlowStats['byType'],
    totalRuns,
    successfulRuns: successRuns,
    failedRuns,
    runningFlows: 0,
    averageRunTime: 0,
    runsLast24Hours: 0,
    runsLast7Days: 0
  };
}

export async function createFlow(payload: CreateFlowPayload): Promise<Flow> {
  const now = new Date().toISOString();
  const flow: Partial<Flow> = {
    ...payload,
    apiName: payload.apiName ?? payload.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, ''),
    status: 'draft',
    variables: (payload.variables ?? []) as FlowVariable[],
    elements: (payload.elements ?? []) as FlowElement[],
    startElementId: payload.startElementId ?? '',
    version: 1,
    versions: [{
      id: generateId('ver'),
      version: 1,
      status: 'draft',
      description: 'Initial version',
      createdDate: now,
      createdBy: 'current_user',
      createdByName: 'System Administrator'
    }],
    createdDate: now,
    createdBy: 'current_user',
    createdByName: 'System Administrator',
    lastModifiedDate: now,
    lastModifiedBy: 'current_user',
    lastModifiedByName: 'System Administrator',
    runCount: 0,
    successCount: 0,
    failureCount: 0
  };
  const created = await createGobodoRecord(getObjectType(), flowToRecord(flow));
  return recordToFlow(created);
}

export async function updateFlow(payload: UpdateFlowPayload): Promise<Flow | null> {
  const existing = await getFlowById(payload.id);
  if (!existing) return null;
  const merged = { ...existing, ...payload } as Flow;
  const updated = await updateGobodoRecord(payload.id, flowToRecord(merged));
  return recordToFlow(updated);
}

export async function deleteFlow(id: string): Promise<boolean> {
  await deleteGobodoRecord(id);
  return true;
}

export async function cloneFlow(payload: CloneFlowPayload): Promise<Flow | null> {
  const original = await getFlowById(payload.sourceFlowId);
  if (!original) return null;
  return createFlow({
    name: payload.newName,
    description: payload.description ?? original.description,
    type: original.type,
    variables: original.variables,
    elements: original.elements,
    startElementId: original.startElementId,
    scheduleConfig: original.scheduleConfig,
    recordTriggerConfig: original.recordTriggerConfig,
    platformEventConfig: original.platformEventConfig
  });
}

export async function activateFlow(id: string): Promise<Flow | null> {
  const f = await getFlowById(id);
  if (!f) return null;
  return updateFlow({ id, status: 'active' });
}

export async function deactivateFlow(id: string): Promise<Flow | null> {
  const f = await getFlowById(id);
  if (!f) return null;
  return updateFlow({ id, status: 'inactive' });
}
